<template>
  <div class="w-full mt-4 md:mt-6 lg:mt-8 space-y-6 md:space-y-8">

    <!-- Spotting Area Actors Section -->
    <div v-if="newSpottingAreaActors.length > 0" class="mb-6 md:mb-8">
      <h3 class="mb-4 text-lg md:text-xl font-medium text-gray-900 px-2 md:px-0">{{
          this.getPreferredSpottingAreaId() !== null ? $t('personal_home_new_spotting_area_actors', { spotting_area: spottingAreaLabel }) : $t('personal_home_new_ecosystem_actors')
        }}</h3>
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
        <div v-for="actor in newSpottingAreaActors" :key="actor.id">
          <simplified-image-card
            :name="actor.name"
            style="background-color: var(--primary-community-extra-lightest); height: 100%"
            :full-size="true"
            :left-align-text="true"
            :image="actor.featured_image_url || actor.website_screenshot || getClearbitActorLogo(actor.url)"
            :link="'/actors/' + actor.id"
            @click="handleClickActorCard(actor)"
            :sub-title="localizedDisplayProperty(actor, 'short_description') || localizedDisplayProperty(actor, 'activities_description')"
          />
        </div>
      </div>
    </div>

    <!-- Challenges Section -->
    <div v-if="challenges.length > 0" class="mb-6 md:mb-8">
      <h3 class="mb-4 text-lg md:text-xl font-medium text-gray-900 px-2 md:px-0">
        {{ $t('personal_home_latest_challenges', { community: this.getPreferredCommunityName ?? $t('personal_home_the_ecosystem') }) }}</h3>
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
        <ChallengeCardNew
          v-for="challenge in challenges"
          :key="challenge.id"
          :challenge="challenge"
          class="h-full"
        />
      </div>
    </div>

    <!-- Announcements Section -->
    <div v-if="announcements.length > 0" class="mb-6 md:mb-8">
      <h3 class="mb-4 text-lg md:text-xl font-medium text-gray-900 px-2 md:px-0">
        {{ $t('personal_home_latest_announcements', { community: getPreferredCommunityName ?? $t('personal_home_the_ecosystem') }) }}</h3>
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
        <simplified-announcement-preview
          v-for="(announcement, index) in announcements"
          :key="announcement.id"
          :is-vertical="true"
          :is-simplified="true"
          :is-container="false"
          :announcement="announcement"
          class="h-full"
        />
      </div>
    </div>

    <!-- Recent Messages Section -->
    <div v-if="missedMessages.length > 0" class="mb-6 md:mb-8">
      <h3 class="mb-4 text-lg md:text-xl font-medium text-gray-900 px-2 md:px-0">{{ $t('personal_home_recent_messages') }}</h3>
      <div class="space-y-3">
        <div v-for="message in missedMessages" :key="message.id"
             class="w-flex flex-row shadow-sm p-3 justify-between items-center cursor-pointer hover:bg-gray-50 mb-2 rounded-md relative"
             @click="openConversation(message.conversationId, message.sender.id)">
          <div class="tw-flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div class="tw-flex flex-col md:flex-row md:items-center items-start space-x-3 min-w-0 flex-1">
              <div class="flex-shrink-0 w-12 h-12 sm:w-10 sm:h-10 min-w-48 max-w-48 overflow-ellipsis">
                <AvatarNew :user="message.sender" show-details/>
              </div>
              <div class="min-w-0 flex-1">
                <div class="text-sm md:ml-3 tw-flex self-center overflow-hidden whitespace-nowrap text-wrap md:text-nowrap text-ellipsis max-w-lg text-gray-600 truncate">
                  {{ message.message?.slice(0, 200) }}
                </div>
              </div>
            </div>
            <div class="text-xs text-gray-500 flex-shrink-0">
              {{ formatMessageTime(message.sendAt) }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Community Members Section -->
    <div v-if="latestCommunityMembers.length > 0" class="mb-8 pb-8">
      <h3 class="mb-4 text-lg md:text-xl font-medium text-gray-900 px-2 md:px-0">{{ $t('personal_home_new_community_members') }}</h3>
      <div class="space-y-3 md:space-y-4">
        <CommunityMemberCard
          v-for="member in latestCommunityMembers"
          :key="member.id"
          :member="member"
          :compact="false"
          :show-actions="true"
          :show-organization="true"
          :show-department="true"
          :show-experience="true"
        />
      </div>
    </div>

    <!-- Empty State -->
    <div v-if="!hasAnyData" class="text-center py-12 md:py-16 px-4">
      <div class="max-w-md mx-auto">
        <div class="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full tw-flex items-center justify-center">
          <svg class="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z"/>
          </svg>
        </div>
        <h3 class="text-lg font-medium text-gray-900 mb-2">{{ $t('personal_home_welcome') }}</h3>
        <p class="text-gray-600">{{ $t('personal_home_welcome_subtitle') }}</p>
      </div>
    </div>
  </div>
</template>

<script>
  import OnboardingMixin from '../../util/OnboardingMixin.js'
  import Tabs from '../Tabs/Tabs.vue'
  import Avatar from '../Avatar/Avatar.vue'
  import TranslationsMixin from '../../util/TranslationsMixin.js'
  import Dropdown from '../Dropdown/Dropdown.vue'
  import UiMixin from '../../util/UiMixin.js'
  import IntroJsMixin from '../../util/IntroJsMixin.js'

  import ToggleButton from '../Form/ToggleButton.vue'
  import { fetchLatestConversation } from '@/api/conversations'
  import AvatarNew from '@/components/Avatar/AvatarNew.vue'
  import { MUTATION_TYPES as UI_MUTATION_TYPES } from '@/store/modules/ui'
  import { fetchLatestAnnouncements, fetchLatestChallenges, fetchLatestMembers } from '@/api/communities'
  import { fetchLatestActors } from '@/api/spottingareas'
  import { getUserPreferences } from '@/api/user'
  import CommunityMemberCard from '@/components/Communities/CommunityMemberCard.vue'
  import ActorMiniCard from '@/components/ActorMiniCard/ActorMiniCard.vue'
  import ChallengeCardNew from '@/components/Challenges/ChallengeCardNew.vue'
  import AnnouncementCard from '@/components/Simplified/AnnouncementCard.vue'
  import ActorInfoCard from '@/components/ActorInfo/ActorInfoCard.vue'
  import SimplifiedImageCard from '@/components/Connection/SimplifiedImageCard.vue'
  import ProfileCardsSection from '@/components/Profile/ProfileCardsSection.vue'
  import AnnouncementOrResourceCard from '@/components/Simplified/AnnouncementOrResourceCard.vue'
  import SimpleArticlePreview from '@/components/SimpleArticlePreview.vue'
  import { getImageForAnnouncement } from '@/util/helpers'
  import SimplifiedAnnouncementPreview from '@/components/Simplified/SimplifiedAnnouncementPreview.vue'
  import { fetchActors } from '@/api/actors'
  import { fetchChallenges } from '@/Domain/Challenge/Api/challenges'
  import { fetchPublicNotifications } from '@/api/notifications'
  import CommunityMixin from '@/util/CommunityMixin'
  import SpottingAreaMixin from '@/util/SpottingAreaMixin'
  import { getLogoDevLogoUrl } from '@/util/actor'
  import { ACTION_TYPES as NOTIFICATION_ACTION_TYPES } from '@/store/modules/notifications'

  export default {
    name: 'PersonalHomeContent',
    data () {
      return {
        componentKey: 1,
        portfolios: [],
        finishedLoadingInitialData: false,
        conceptSearches: [],
        availableResources: [],
        relevantTags: [],
        acceptedTags: [],
        relevantTaxonomies: [],
        acceptedTaxonomies: [],
        updatingAllowPersonalisedContentOption: false,
        missedMessages: [],
        latestCommunityMembers: [],
        newSpottingAreaActors: [],
        challenges: [],
        announcements: [],
        userPreferences: {
          preferredSpottingAreaId: null,
          preferredCommunityId: null,
        },
        highlightedSection: null,
        featuredNotifications: [],
        nonFeaturedAnnouncements: [],
      }
    },
    computed: {
      profile () {
        return this.$store.state.user.profile
      },
      hasAnyData () {
        return this.missedMessages.length > 0 ||
          this.latestCommunityMembers.length > 0 ||
          this.newSpottingAreaActors.length > 0 ||
          this.challenges.length > 0 ||
          this.announcements.length > 0
      },
      showPreferencesGuidance () {
        // Show guidance if user hasn't set preferences and has access to communities or spotting areas
        const hasNoCommunityPreference = !this.userPreferences.preferredCommunityId && this.hasJoinedCommunities
        const hasNoSpottingAreaPreference = !this.userPreferences.preferredSpottingAreaId && this.hasAccessToSpottingAreas

        return hasNoCommunityPreference || hasNoSpottingAreaPreference
      },
      hasJoinedCommunities () {
        return this.profile && this.profile.joinedCommunityIds && this.profile.joinedCommunityIds.length > 0
      },
      hasAccessToSpottingAreas () {
        // Check if user has access to monitoring and has accessible spotting areas
        return this.$store.getters.hasAccessToMonitoring &&
          this.profile &&
          this.profile.accessibleSpottingAreas &&
          this.profile.accessibleSpottingAreas.length > 0
      },
      getPreferredCommunityName () {
        let community = this.communities.filter((community) => {
          return community.id === this.getPreferredCommunityId()
        })

        if (community.length > 0) {
          return community[0].name
        }

        return null
      },
    },
    methods: {
      getClearbitActorLogo: getLogoDevLogoUrl,
      async fetchPublicNotifications () {
        if (!this.$store.getters.canUsePublicAnnouncements) {
          this.atLeastOneElementIsLoading = false

          return {}
        }

        await fetchPublicNotifications(2, 0, { featured: true, is_event: false })
          .then(items => {
            this.featuredNotifications = items || []
            this.atLeastOneElementIsLoading = false
          })

        await fetchPublicNotifications(2, 0, { featured: false, is_event: false })
          .then(items => {
            this.nonFeaturedAnnouncements = items || []
            this.atLeastOneElementIsLoading = false
          })

        this.announcements = [...this.featuredNotifications, ...this.nonFeaturedAnnouncements].slice(0, 2)
        console.log(this.announcements)
      },
      getImageForAnnouncement,
      async loadUserPreferences () {
        try {
          const response = await getUserPreferences()
          this.userPreferences = response.preferences
        } catch (error) {
          console.error('Error loading user preferences:', error)
        }
      },
      getPreferredCommunityId () {
        // Use preferred community if set, otherwise fall back to first available
        if (this.userPreferences.preferredCommunityId) {
          return this.userPreferences.preferredCommunityId
        }
        if (this.profile && this.profile.joinedCommunityIds && this.profile.joinedCommunityIds.length > 0) {
          return this.profile.joinedCommunityIds[0]
        }
        return null
      },
      getPreferredSpottingAreaId () {
        // Use preferred spotting area if set, otherwise fall back to first available
        if (this.userPreferences.preferredSpottingAreaId) {
          return this.userPreferences.preferredSpottingAreaId
        }
        if (this.profile && this.profile.accessibleSpottingAreas && this.profile.accessibleSpottingAreas.length > 0) {
          return this.profile.accessibleSpottingAreas[0]
        }
        return null
      },
      handleClickActorCard (actor) {
        const spottingAreaId = this.getPreferredSpottingAreaId()
        this.$router.push(`/spotting-areas/${spottingAreaId}/actors/${actor.id}`)
      },
      openConversation (conversationId, userId) {
        this.$router.push('/profile/conversations/' + conversationId).then(() => {
          this.$store.commit(UI_MUTATION_TYPES.SHOW_SIDE_PANEL, {
            component: 'user-information-side-panel',
            metaData: {
              userId: userId,
              allowDelete: false,
            },
          })
        })
      },
      fetchLatestConversationMessages () {
        fetchLatestConversation()
          .then((response) => {
            this.missedMessages = response
          })
      },
      fetchLatestCommunityMembers () {
        const communityId = this.getPreferredCommunityId()
        if (communityId) {
          fetchLatestMembers({ communityId })
            .then((response) => {
              this.latestCommunityMembers = response.filter((user) => {
                return user.id !== this.profile.id
              })
            })
        }
      },
      fetchNewSpottingAreaActors () {
        const spottingAreaId = this.getPreferredSpottingAreaId()
        if (spottingAreaId) {
          fetchLatestActors({ spottingAreaId })
            .then((response) => {
              this.newSpottingAreaActors = response.slice(0, 3) // Show latest 3 actors
            })
            .catch((error) => {
              console.error('Error fetching spotting area actors:', error)
            })
        } else {
          fetchActors({ limit: 3 })
            .then((response) => {
              this.newSpottingAreaActors = response.data.slice(0, 3)
            })
            .catch((error) => {
              console.error('Error fetching spotting area actors:', error)
            })
        }
      },
      fetchLatestChallenges () {
        const communityId = this.getPreferredCommunityId()
        if (communityId) {
          fetchLatestChallenges({ communityId, limit: 3 })
            .then((response) => {
              this.challenges = response
            })
            .catch((error) => {
              console.error('Error fetching challenges:', error)
            })
        } else {
          fetchChallenges({ limit: 3, offset: 0, filters: [] })
            .then((response) => {
              this.challenges = response
            })
            .catch((error) => {
              console.error('Error fetching challenges:', error)
            })
        }
      },
      fetchLatestAnnouncements () {
        const communityId = this.getPreferredCommunityId()
        if (communityId) {
          fetchLatestAnnouncements({ communityId, limit: 2 })
            .then((response) => {
              this.announcements = response
            })
            .catch((error) => {
              console.error('Error fetching announcements:', error)
            })
        } else {
          this.fetchPublicNotifications()
        }
      },
      formatMessageTime (timestamp) {
        return new Date(timestamp).toLocaleDateString()
      },
      highlightCommunitySection () {
        this.highlightedSection = 'community'
        this.$nextTick(() => {
          // Emit an event to the ProfileCardsSection component to highlight the community card
          this.$bus.emit('highlightCommunityCard')

          // Wait a moment for the highlight to be applied, then scroll
          setTimeout(() => {
            // Scroll to the community card specifically
            const communityCard = document.querySelector('.community-card')
            if (communityCard) {
              communityCard.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
                inline: 'nearest'
              })
            } else {
              // Fallback to profile cards section
              const targetElement = document.querySelector('.profile-cards-section')
              if (targetElement) {
                targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' })
              }
            }
          }, 100)

          // Remove highlight after 6 seconds
          setTimeout(() => {
            this.highlightedSection = null
            this.$bus.emit('removeHighlight')
          }, 6000)
        })
      },
      highlightSpottingAreaSection () {
        this.highlightedSection = 'spotting-area'
        this.$nextTick(() => {
          // Emit an event to the ProfileCardsSection component to highlight the spotting area card
          this.$bus.emit('highlightSpottingAreaCard')

          // Wait a moment for the highlight to be applied, then scroll
          setTimeout(() => {
            // Scroll to the spotting area card specifically
            const spottingAreaCard = document.querySelector('.spotting-area-card')
            if (spottingAreaCard) {
              spottingAreaCard.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
                inline: 'nearest'
              })
            } else {
              // Fallback to profile cards section
              const targetElement = document.querySelector('.profile-cards-section')
              if (targetElement) {
                targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' })
              }
            }
          }, 100)

          // Remove highlight after 6 seconds
          setTimeout(() => {
            this.highlightedSection = null
            this.$bus.emit('removeHighlight')
          }, 6000)
        })
      },
      async resetUserObjects () {
        // Refresh data when user profile is updated
        await this.loadUserPreferences()
        await this.fetchLatestCommunityMembers()
        await this.fetchNewSpottingAreaActors()
        await this.fetchLatestChallenges()
        await this.fetchLatestAnnouncements()
      },
    },
    created () {
      this.$bus.on('updateUserProfile', this.resetUserObjects)
    },
    async mounted () {
      this.fetchLatestConversationMessages()

      // Load user preferences first, then fetch data based on preferences
      await this.loadUserPreferences()
      await this.fetchLatestCommunityMembers()
      await this.fetchNewSpottingAreaActors()
      await this.fetchLatestChallenges()
      await this.fetchLatestAnnouncements()
      this.$store.dispatch(NOTIFICATION_ACTION_TYPES.FETCH_NOTIFICATIONS_LIST, {
        limit: this.$store.state.notifications.listData.fetchLimit,
        offset: 0,
      })
    },
    beforeUnmount () {
      this.$bus.off('updateUserProfile', this.resetUserObjects)
    },
    mixins: [OnboardingMixin, TranslationsMixin, UiMixin, IntroJsMixin, CommunityMixin, SpottingAreaMixin],
    components: {
      SimplifiedAnnouncementPreview,
      SimpleArticlePreview,
      AnnouncementOrResourceCard,
      ProfileCardsSection,
      SimplifiedImageCard,
      ActorInfoCard,
      AnnouncementCard,
      ChallengeCardNew,
      ActorMiniCard,
      CommunityMemberCard,
      AvatarNew,
      Tabs,
      Avatar,
      Dropdown,
      ToggleButton
    },
  }
</script>

<style lang="scss" scoped>
  @import "../../../scss/_variables.scss";

  .ph__allow-personalisation-container {
    display: flex;
    height: 3rem;
    align-items: center;
    justify-content: center;
  }

  .ph__card-container {
    @media (max-width: $screen-lg) {
      margin-bottom: 20px;
      &:last-child {
        margin-bottom: 0;
      }
    }
  }

  .ph__card {
    box-shadow: $simplified-box-shadow-config;
    border-radius: $default-border-radius;
    position: relative;
    margin-bottom: 20px;

    &.ph__action-card {
      margin-bottom: 0;
      padding: 10px;
      display: flex;
      flex-direction: column;

      &:last-child {
        margin-right: 0;
      }

      .ph__action-card-image {
        background-repeat: no-repeat;
        background-size: cover;
        background-position: center center;
        width: 100%;
      }
    }

    &.with-tabs {
      background-color: var(--primary-extra-lightest);
    }
  }

  .ph__welcome-text {
    padding: 20px 70px;
  }

  h4.h4 {
    margin-bottom: 10px;
    text-align: center;
  }

  p {
    margin-bottom: 20px;
    line-height: 15px;
    letter-spacing: 0.5px;
    text-align: center;

    &:last-child {
      margin-bottom: 0;
    }
  }

  .ph__tabs-content {
    padding: 20px;
  }

  :deep(.button__label) {
    overflow-x: hidden;
    display: block;
    text-overflow: ellipsis;
    text-align: center;
  }

  .ph__card-title {
    width: 100%;
    overflow: hidden;
    white-space: nowrap;
    height: 16px;
    font-size: 14px;
    text-overflow: ellipsis;
    margin: 10px 0;
  }

  .ph__card-description {
    $lineHeight: 15px;
    $nrOfLines: 3;
    display: -webkit-box;
    width: 100%;
    height: $nrOfLines*$lineHeight;
    -webkit-line-clamp: $nrOfLines;
    line-height: $lineHeight;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    font-size: 12px;
    margin-bottom: 15px;

    &.announcement-description {
      $lineHeight: 15px;
      $nrOfLines: 6;
      height: $nrOfLines*$lineHeight;
      -webkit-line-clamp: $nrOfLines;
    }

    &.resource-description {
      $lineHeight: 15px;
      $nrOfLines: 6;
      height: $nrOfLines*$lineHeight;
      -webkit-line-clamp: $nrOfLines;
    }

    p {
      text-align: left;
    }
  }

  hr {
    flex-grow: 1;
    border: 1px solid var(--primary-extra-lightest);
    height: 0;
  }

  .ph__relevant-yes-no {
    margin: 10px 0;
    font-size: 12px;

    span {
      line-height: 10px;
      border: 1px solid var(--primary);
      border-radius: $default-border-radius-narrow;
      margin: 5px 5px 10px 0;
      cursor: pointer;
      padding: 5px 10px;
      display: inline-block;

      &.is-active {
        background: var(--primary-extra-lightest);
      }
    }
  }

  .ph__announcement-creator {
    display: flex;

    .ph__announcement-creator-name {
      white-space: nowrap;
      text-overflow: ellipsis;
      overflow-x: hidden;
      line-height: 22px;
    }
  }

  .ph__resource-title {
    font-size: 14px;
    font-weight: 500;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  :deep(.multiselect.multiselect--datascouts) {
    .multiselect__tags {
      padding: 0;
      border: 0
    }

    &.multiselect--active {
      .multiselect__tags {
        box-shadow: none !important; // important to override important in _focus-state.scss
      }
    }

    .multiselect__content-wrapper {
      display: none;
    }

    .multiselect__placeholder {
      display: none;
    }
  }

  .ph__resource-description {
    $lineHeight: 16px;
    $nrOfLines: 2;
    margin-bottom: 10px;
    margin-top: 5px;
    $lineHeight: 16px;
    $nrOfLines: 6;
    display: -webkit-box;
    width: 100%;
    height: $nrOfLines*$lineHeight;
    -webkit-line-clamp: $nrOfLines;
    line-height: $lineHeight;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    font-size: 12px;
  }
</style>
